import templateImage from '@/features/destinations/components/template-image';
import RouteConstants from '@/features/router/RouteConstants';
import { useWorkspace } from '@/features/workspaces/hooks';
import { StepPageActions, StepPageHeader } from '@/global/components';
import { StepPageActionsButton } from '@/global/components/Layout/StepPageActions/types';
import { Input, SpinnerIcon } from '@/lib';
import { Select } from '@/lib/Selects';
import { BaseAudience } from '@/models/audiences';
import {
  CyclrActionParameter,
  CyclrActionTriggerValue,
  DISTRIBUTION_CHANNEL_STATUS,
  DistributionChannelContent,
  DistributionChannelParameterUpdate,
} from '@/models/distributionChannels';
import { ISelectItem } from '@/models/select';
import { useGetAllAudiencesQuery } from '@/services/endpoints/audiences';
import {
  useCreateDistributionChannelMutation,
  useDistributionChannelAuthenticatedMutation,
  useGetDistributionChannelConnectorsQuery,
  useUpdateDistributionChannelParametersMutation,
} from '@/services/endpoints/distributionChannels';
import { CyclrTemplate, useCyclrTemplatesQuery } from '@/services/endpoints/integrations';
import { popupCenter } from '@/utils';
import TargetIcon from '@assets/icons/target.svg?react';
import UserPlusIcon from '@assets/icons/user-plus.svg?react';
import { ArrowRightIcon, UserGroupIcon } from '@heroicons/react/solid';
import { Fragment, FunctionComponent, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

type AudienceAndDestinationStepProps = {
  distributionChannelContent?: DistributionChannelContent;
  nextStep: (audience: BaseAudience, template: CyclrTemplate, distributionChannelContent: DistributionChannelContent) => void;
  onCancel: VoidFunction;
  forceAudience?: BaseAudience;
  forceTemplate?: CyclrTemplate;
};

export const AudienceAndDestinationStep: FunctionComponent<AudienceAndDestinationStepProps> = ({
  nextStep,
  onCancel,
  distributionChannelContent,
  forceAudience,
  forceTemplate,
}) => {
  const { t } = useTranslation(['COMMON', 'ALL']);
  const workspace = useWorkspace();

  const { data: audiences, isLoading: audienceIsLoading } = useGetAllAudiencesQuery({
    workspaceId: workspace.id,
  });

  const { data: templates, isLoading: templateIsLoading } = useCyclrTemplatesQuery({
    workspaceId: workspace.id,
  });

  const { data: connectors } = useGetDistributionChannelConnectorsQuery({ workspaceId: workspace.id });

  const [createDistributionChannel, { isLoading: isCreateLoading, data: createData }] = useCreateDistributionChannelMutation();

  const [distributionChannelAuthenticated, { data: getData }] = useDistributionChannelAuthenticatedMutation();

  const [updateDistributionChannelParameters, { isLoading: isUpdateParametersLoading, data: updateParametersData }] =
    useUpdateDistributionChannelParametersMutation();

  const connectorImageOrTemplateImage = (template: CyclrTemplate) => {
    const connector = template.Connectors.find((item) => item.Name !== 'Generic Webhook' && item.Name !== 'Global Data Storage');
    if (connector && connector.Icon) {
      return <img className={'w-5'} src={connector.Icon} />;
    } else {
      return templateImage(template.Name);
    }
  };

  const [selectedAudience, setSelectedAudience] = useState<(ISelectItem & BaseAudience) | undefined>(
    forceAudience
      ? {
          ...forceAudience,
          label: forceAudience.name,
          image: <UserGroupIcon className={'size-5 text-gray-500'} />,
        }
      : undefined
  );
  const [selectedTemplate, setSelectedTemplate] = useState<(ISelectItem & CyclrTemplate) | undefined>(
    forceTemplate
      ? {
          ...forceTemplate,
          id: forceTemplate.Id,
          label: forceTemplate.Name,
          image: connectorImageOrTemplateImage(forceTemplate),
        }
      : undefined
  );

  const [status, setStatus] = useState<string>(distributionChannelContent?.integrationsResult.cyclr.status || DISTRIBUTION_CHANNEL_STATUS.CREATE);
  const [distributionChannel, setDistributionChannel] = useState<DistributionChannelContent | undefined>(distributionChannelContent);
  const [selectedParameters, setSelectedParameters] = useState<{ [id: string]: DistributionChannelParameterUpdate }>({});
  const [triggerParam, setTriggerParam] = useState<{ [paramId: string]: CyclrActionTriggerValue | undefined }>({});

  useEffect(() => {
    const tempDistributionChannel = getData || createData || distributionChannel;
    setDistributionChannel(tempDistributionChannel);
    setStatus(tempDistributionChannel?.integrationsResult.cyclr.status || status);
    if (tempDistributionChannel?.integrationsResult.cyclr.status === DISTRIBUTION_CHANNEL_STATUS.AUTHENTICATION_REQUIRED) {
      // window.open(createData.integrationsResult.cyclr.action.authUrl, '__blank');
      const action = tempDistributionChannel.integrationsResult.cyclr.action;
      authenticationScreen(
        `${action.authUrl}?id=${action.accountConnectors[0]}&token=${action.token}&targetOrigin=${location.origin + RouteConstants.cyclrAuthRedirect}`,
        tempDistributionChannel as any
      );
    } else if (tempDistributionChannel?.integrationsResult.cyclr.status === DISTRIBUTION_CHANNEL_STATUS.ACTIVABLE) {
      nextStep(selectedAudience, selectedTemplate, tempDistributionChannel);
    }
  }, [createData, getData]);

  useEffect(() => {
    if (updateParametersData) {
      nextStep(selectedAudience, selectedTemplate, updateParametersData);
    }
  }, [updateParametersData]);

  const authenticationScreen = (url: string, distributionChannel: DistributionChannelContent) => {
    (window as any).cyclrAuthenticationCompleted = (success: boolean, w: Window) => {
      w.close.apply(w);
      delete (window as any).cyclrAuthenticationCompleted;
      distributionChannelAuthenticated({
        workspaceId: workspace.id,
        audienceId: selectedAudience?.id as any,
        distributionChannelId: distributionChannel?.distributionChannel.id as any,
      });
    };
    (window as any).authWindow = popupCenter({
      url,
      title: t('ALL:destinations.authenticate'),
      w: 640,
      h: 640,
    });

    const checkWindowClosed = setInterval(() => {
      if (!(window as any).authWindow || (window as any).authWindow.closed) {
        clearInterval(checkWindowClosed);

        setStatus(DISTRIBUTION_CHANNEL_STATUS.CREATE);
        setDistributionChannel(undefined);
      }
    }, 500);
  };

  const saveDestination = () => {
    createDistributionChannel({
      workspaceId: workspace.id,
      audienceId: selectedAudience?.id as string,
      configData: {
        templateId: selectedTemplate?.Id,
        templateName: selectedTemplate?.Name,
      },
    });
  };

  const updateParameters = () => {
    updateDistributionChannelParameters({
      workspaceId: workspace.id,
      audienceId: selectedAudience?.id as string,
      distributionChannelId: distributionChannel?.distributionChannel.id as string,
      parameters: Object.values(selectedParameters),
    });
  };

  const parameterValueChange = (update: DistributionChannelParameterUpdate) => {
    setSelectedParameters({ ...selectedParameters, [update.parameterId]: update });
  };

  const parameterTemplate = (param: CyclrActionParameter, stepIds?: string[], index = 1) => {
    const translationKey = distributionChannel?.distributionChannel?.name.toLowerCase().replace(/\s+/g, '_');
    const { name, triggerParameterLookupValues } = param;
    const paramName = (triggerParameterLookupValues?.trigger?.Name || name).toLowerCase().replace(/\s+/g, '_');
    return (
      <Fragment key={param.parameterId}>
        <div className={'flex h-auto flex-col gap-2 border-t border-gray-200 py-6 text-left font-medium'}>
          {paramName && (
            <h3 className={'text-sm font-medium leading-5 text-tertiary-600'}>
              {t(`ALL:destinations.steps.selectAudienceAndDestination.cyclr.${translationKey}.${paramName}_title` as any)}
            </h3>
          )}
          {param.triggerParameterLookupValues?.values?.length == 0 && (
            <p className={'mt-5 w-full text-base font-medium italic text-gray-500 md:w-2/3 lg:w-1/2'}>
              {t(`ALL:destinations.steps.selectAudienceAndDestination.cyclr.${translationKey}.${paramName}_empty` as any)}
            </p>
          )}
          {param.mappingType !== null ? (
            <Select
              items={param.triggerParameterLookupValues?.values.map<ISelectItem>((lookupValue) => ({
                ...lookupValue,
                id: lookupValue?.name,
                label: lookupValue?.name,
              }))}
              value={
                selectedParameters[param.parameterId]
                  ? {
                      id: selectedParameters[param.parameterId]?.name,
                      label: selectedParameters[param.parameterId]?.name,
                    }
                  : undefined
              }
              onChange={(value: ISelectItem) => {
                const lookupValue = value as ISelectItem & CyclrActionTriggerValue;
                if (lookupValue.nextTrigger) {
                  setTriggerParam({
                    ...triggerParam,
                    [index]: lookupValue,
                  });
                } else {
                  parameterValueChange({
                    isAccountConnectorParameter: param.isAccountConnectorParameter,
                    accountConnectorId: param.accountConnectorId,
                    propertyId: param.propertyId,
                    stepIds: stepIds || param.stepIds,
                    parameterId: param.parameterId,
                    mappingType: param.mappingType,
                    value: lookupValue.value,
                    name: lookupValue.name,
                  });
                }
              }}
            />
          ) : (
            <Input
              className={'w-full bg-primary-background text-base font-normal md:w-2/3 lg:w-1/2'}
              placeholder={t(`ALL:destinations.steps.selectAudienceAndDestination.cyclr.${translationKey}.${paramName}` as any)}
              onChange={(e) => {
                parameterValueChange({
                  isAccountConnectorParameter: param.isAccountConnectorParameter,
                  accountConnectorId: param.accountConnectorId,
                  propertyId: param.propertyId,
                  stepIds: param.stepIds,
                  parameterId: param.parameterId,
                  mappingType: 'StaticValue',
                  value: e.target.value,
                  name: null,
                });
              }}
            />
          )}
        </div>

        {triggerParam[index]?.nextTrigger &&
          parameterTemplate(
            {
              triggerParameterLookupValues: {
                values: triggerParam[index]?.nextTrigger?.values,
              },
              name: triggerParam[index]?.nextTrigger?.trigger.Name,
              description: '',
              parameterId: param.parameterId,
              mappingType: param.mappingType,
            } as any,
            stepIds || param.stepIds,
            index + 1
          )}
      </Fragment>
    );
  };

  const statusTemplate = () => {
    switch (status) {
      case DISTRIBUTION_CHANNEL_STATUS.PARAMETERS_REQUIRED:
        return (
          <div className={'mt-8'}>
            {distributionChannel?.integrationsResult.cyclr.action.parameters.filter((param) => !param.isOptional).map((param) => parameterTemplate(param))}
          </div>
        );

      case DISTRIBUTION_CHANNEL_STATUS.AUTHENTICATION_REQUIRED:
      case DISTRIBUTION_CHANNEL_STATUS.CREATE:
      default:
        return <Fragment />;
    }
  };

  const actionButton = () => {
    const cancelButton: StepPageActionsButton = { text: 'COMMON.cancel', onClick: onCancel };

    switch (status) {
      case DISTRIBUTION_CHANNEL_STATUS.CREATE:
        return (
          <StepPageActions
            primaryButton={{
              text: 'COMMON.confirm',
              isLoading: isCreateLoading,
              onClick: saveDestination,
              disabled: !selectedAudience || !selectedTemplate,
            }}
            secondaryButton={cancelButton}
          />
        );

      case DISTRIBUTION_CHANNEL_STATUS.PARAMETERS_REQUIRED:
        return (
          <StepPageActions
            primaryButton={{
              text: 'COMMON.confirm',
              isLoading: isUpdateParametersLoading,
              onClick: updateParameters,
              disabled: !!distributionChannel?.integrationsResult.cyclr.action.parameters
                .filter((param) => !param.isOptional)
                .find((param) => {
                  return !selectedParameters[param.parameterId] || !selectedParameters[param.parameterId].value;
                }),
            }}
            secondaryButton={cancelButton}
          />
        );

      case DISTRIBUTION_CHANNEL_STATUS.AUTHENTICATION_REQUIRED:
        return (
          <div className='flex'>
            <span className='mr-3 italic text-gray-800'>{t('ALL:destinations.authenticating')}</span>
            <SpinnerIcon className='size-6' />
          </div>
        );

      default:
        return (
          <StepPageActions
            primaryButton={{
              text: 'COMMON.confirm',
            }}
            secondaryButton={cancelButton}
          />
        );
    }
  };

  const audiencesSelectItems = useMemo(
    () =>
      audiences?.audiences.map<ISelectItem & BaseAudience>((audience) => ({
        ...audience,
        label: audience.name,
        image: <UserGroupIcon className={'size-5 text-gray-500'} />,
      })) || [],
    [audiences?.audiences]
  );

  const templateSelectItems = useMemo(
    () =>
      templates?.map<ISelectItem & CyclrTemplate>((template) => ({
        ...template,
        id: template.Id,
        label: template.Name,
        image: connectorImageOrTemplateImage(template),
        disabled: connectors?.find((c) => c.name === template.Name)?.installed === false,
        disabledText: 'disconnected',
      })) || [],
    [templates]
  );

  return (
    <>
      <div className={'flex grow flex-col gap-6 p-6'}>
        <StepPageHeader title='destinations:edit.step.destination.title' subtitle='destinations:edit.step.destination.text' />
        <div className='flex w-full'>
          <div className='w-1/2'>
            <Select
              isLoading={audienceIsLoading}
              disabled={!!forceAudience || !!distributionChannel || isCreateLoading}
              placeholderImage={<UserPlusIcon className='size-6 text-brand-600' />}
              items={audiencesSelectItems}
              value={selectedAudience}
              onChange={(value) => setSelectedAudience(value as ISelectItem & BaseAudience)}
            />
          </div>
          <ArrowRightIcon height={'20px'} className={'mt-2 px-5 text-gray-500'} />
          <div className='w-1/2'>
            <Select
              isLoading={templateIsLoading}
              disabled={!!distributionChannel || isCreateLoading}
              placeholderImage={<TargetIcon className='size-6 text-brand-600' />}
              items={templateSelectItems}
              value={selectedTemplate}
              onChange={(value) => setSelectedTemplate(value as ISelectItem & CyclrTemplate)}
            />
          </div>
        </div>
        {statusTemplate()}
      </div>

      {/* Actions Container */}
      {actionButton()}
    </>
  );
};
